import {useEffect, useId, useState} from 'react';
import api from '@/lib/api';

export default function XeroContactSelect({value, onChange, disabled, id, initialContact}) {
  const uid = useId();
  const listId = `${uid}-results`;
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(initialContact || null);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);
  useEffect(() => {
    let active = true;
    setRows([]); setIndex(-1); setLoading(true); setError('');
    const timer = setTimeout(async () => {
      try {
        const {data} = await api.get('/xero/contacts/search', {params: {q: query, contact_id: value || undefined}});
        if (!active) return;
        setRows(data.items || []); setMore(!!data.has_more);
        setSelected(data.selected || null);
      } catch { if (active) setError('Could not load Xero contacts. Try searching again.'); }
      finally { if (active) setLoading(false); }
    }, 200);
    return () => { active = false; clearTimeout(timer); };
  }, [query, value]);
  useEffect(() => {
    if (open && index >= 0) document.getElementById(`${listId}-${index}`)?.scrollIntoView?.({block: 'nearest'});
  }, [open, index, listId]);
  const choose = c => { onChange(c.xero_contact_id, c); setSelected(c); setOpen(false); setQuery(''); };
  const details = c => [c.email, c.contact_number].filter(Boolean).join(' · ');
  return <div className="relative min-w-0" onBlur={e => {if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);}}>
    <div className="flex gap-2">
      <input id={id} role="combobox" aria-label="Search Xero contacts" aria-autocomplete="list" aria-expanded={open}
        aria-controls={listId} aria-activedescendant={open && index >= 0 ? `${listId}-${index}` : undefined}
        className="w-full min-w-0 rounded border border-zinc-300 bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5F15] dark:border-zinc-700"
        placeholder="Search name, email or contact number" value={query} disabled={disabled}
        onFocus={() => setOpen(true)} onChange={e => {setQuery(e.target.value); setOpen(true); setIndex(-1);}}
        onKeyDown={e => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault(); setOpen(true);
            setIndex(i => rows.length ? (e.key === 'ArrowDown' ? (i + 1) % rows.length : (i <= 0 ? rows.length - 1 : i - 1)) : -1);
          } else if (e.key === 'Enter' && open && index >= 0 && rows[index]) {e.preventDefault(); choose(rows[index]);}
          else if (e.key === 'Escape') {setOpen(false); setIndex(-1);}
        }}/>
      {value && <button type="button" disabled={disabled} className="text-sm underline" onClick={() => {onChange('', null); setSelected(null); setQuery('');}}>Clear selection</button>}
    </div>
    {value && <div className="mt-1 break-words text-xs text-muted-foreground">
      <span className="font-semibold">Selected: {selected?.name || value}</span>{selected && details(selected) && <span> · {details(selected)}</span>}
      {!loading && !error && (!selected || selected.eligible === false || selected.contact_status !== 'ACTIVE') && <p role="alert" className="text-orange-700 dark:text-orange-300">This contact is inactive or unverified. Sync contacts and select an active replacement before export.</p>}
    </div>}
    {open && <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded border border-zinc-300 bg-background p-1 shadow-lg dark:border-zinc-700">
      <div role="status" className="px-2 text-xs text-muted-foreground">{loading ? 'Loading contacts…' : error || (!rows.length ? 'No matching contacts. Try another search or sync contacts in Xero settings.' : more ? 'Showing 25 matches. Refine your search.' : '')}</div>
      <ul id={listId} role="listbox" aria-label="Xero contacts" aria-busy={loading}>
        {!loading && rows.map((c, i) => <li key={c.xero_contact_id} id={`${listId}-${i}`} role="option" aria-selected={value === c.xero_contact_id}
          className={`cursor-pointer rounded px-3 py-2 text-sm ${index === i ? 'bg-orange-100 dark:bg-orange-950' : 'hover:bg-muted'}`}
          onMouseDown={e => e.preventDefault()} onClick={() => choose(c)}>
          <div className="font-semibold break-words">{c.name}</div><div className="break-words text-xs text-muted-foreground">{details(c)}</div>
        </li>)}
      </ul>
    </div>}
  </div>;
}
